/**
 * Asistente con IA (diseño B2): panel flotante a la derecha que no empuja la página. «Recoger ›»
 * lo guarda en una pestaña del borde con la conversación intacta; «＋» empieza otra.
 * The browser only talks to /api/asistente (admin): the server calls the AI with read-only tools.
 * Without the AI (no key, provider down) it falls back to «Modo local»: navigation only.
 */
const AIAssistant = {
    started: false,
    opened: false,
    collapsed: true,
    available: null,      // null = unknown, true = AI, false = Modo local
    busy: false,
    unread: false,
    tab: 'chat',
    conv: null,           // { id, startedAt, messages: [{ role, text, steps, nav, error, pending }] }
    history: [],          // earlier conversations of this tab (memory only)
    suggestions: null,    // { learned, items }
    proposals: new Map(), // id → proposal (not saved until «Programar»)
    elements: {},
    LOCAL_LINKS: [['calendario-formacion', 'Programación lectiva'], ['profesores', 'Profesores'], ['calendario', 'Calendario general'], ['formaciones', 'Formaciones']],

    init() {
        if (this.started) return;
        this.started = true;
        this.conv = this.newConversation();
        this.render();
        this.bindEvents();
    },

    newConversation() { return { id: Date.now() + '-' + Math.random().toString(36).slice(2, 8), startedAt: new Date(), messages: [] }; },

    icon(kind) {
        const paths = {
            spark: '<path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"></path><path d="M19 15l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z"></path>',
            plus: '<path d="M12 5v14M5 12h14"></path>',
            pin: '<path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z"></path><circle cx="12" cy="10" r="2.5"></circle>',
            send: '<path d="M5 12h14M13 6l6 6-6 6"></path>'
        };
        return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[kind]}</svg>`;
    },

    render() {
        const root = document.createElement('div');
        root.id = 'aiw-root';
        SafeHTML.set(root, `
            <aside class="aiw-panel aiw-collapsed" id="aiw-panel" aria-label="Asistente" aria-hidden="true">
                <header class="aiw-head">
                    <div class="aiw-head-row">
                        <span class="aiw-logo">${this.icon('spark')}</span>
                        <h2 class="aiw-title">Asistente</h2>
                        <span class="aiw-badge" id="aiw-badge">…</span>
                        <span class="aiw-grow"></span>
                        <button type="button" class="aiw-btn aiw-icon-btn" data-aiw="new" aria-label="Nueva conversación" title="Nueva conversación">${this.icon('plus')}</button>
                        <button type="button" class="aiw-btn" data-aiw="collapse" title="La conversación se queda guardada">Recoger ›</button>
                    </div>
                    <div class="aiw-tabs" role="tablist">
                        <button type="button" role="tab" class="aiw-tab" data-aiw="tab" data-tab="chat" id="aiw-tab-chat">Conversación</button>
                        <button type="button" role="tab" class="aiw-tab" data-aiw="tab" data-tab="hist" id="aiw-tab-hist">Historial de hoy</button>
                    </div>
                </header>
                <div class="aiw-chat" id="aiw-chat">
                    <div class="aiw-context" id="aiw-context"></div>
                    <div class="aiw-list" id="aiw-list" aria-live="polite"></div>
                    <div class="aiw-sugs" id="aiw-sugs"></div>
                    <form class="aiw-form" id="aiw-form">
                        <label for="aiw-input" class="aiw-label">Tu pregunta</label>
                        <div class="aiw-form-row">
                            <textarea id="aiw-input" class="aiw-input" rows="2" maxlength="4000" placeholder="Pregunta sobre esta página o cualquier otra cosa de SICAF"></textarea>
                            <button type="submit" class="aiw-send" id="aiw-send">Enviar</button>
                        </div>
                    </form>
                </div>
                <div class="aiw-hist" id="aiw-hist" hidden></div>
            </aside>
            <button type="button" class="aiw-edge" id="aiw-edge" data-aiw="expand" aria-label="Abrir el asistente" hidden>
                ${this.icon('spark')}<span class="aiw-edge-text">Asistente</span><span class="aiw-edge-state" id="aiw-edge-state"></span>
            </button>
            <div class="aiw-edge-note" id="aiw-edge-note" role="status" hidden></div>
        `);
        document.body.appendChild(root);
        const $ = id => document.getElementById(id);
        this.elements = { root, panel: $('aiw-panel'), badge: $('aiw-badge'), context: $('aiw-context'), list: $('aiw-list'), sugs: $('aiw-sugs'),
            form: $('aiw-form'), input: $('aiw-input'), send: $('aiw-send'), chat: $('aiw-chat'), hist: $('aiw-hist'), edge: $('aiw-edge'),
            edgeState: $('aiw-edge-state'), edgeNote: $('aiw-edge-note'), tabChat: $('aiw-tab-chat'), tabHist: $('aiw-tab-hist') };
        this.renderAll();
    },

    bindEvents() {
        document.addEventListener('click', event => {
            const trigger = event.target.closest?.('#ai-assistant-trigger, a[href="#ai-assistant"]');
            if (!trigger) return;
            event.preventDefault();
            event.stopPropagation();
            this.toggle();
        });
        const root = this.elements.root;
        root.addEventListener('click', event => {
            const el = event.target.closest('[data-aiw]');
            if (!el || !root.contains(el)) return;
            const act = el.dataset.aiw;
            if (act === 'collapse') this.collapse();
            else if (act === 'expand') this.open();
            else if (act === 'new') this.startNew();
            else if (act === 'tab') { this.tab = el.dataset.tab; this.renderAll(); }
            else if (act === 'steps') { const m = this.conv.messages[Number(el.dataset.i)]; if (m) { m.open = !m.open; this.renderList(); } }
            else if (act === 'nav') this.go(el.dataset.route);
            else if (act === 'sug') this.useSuggestion(Number(el.dataset.i));
            else if (act === 'hide') this.hideSuggestion(el.dataset.key);
            else if (act === 'forget') this.forget();
            else if (act === 'past') this.openPast(Number(el.dataset.i));
            else if (act.startsWith('p-')) this.proposalAction(act, el);
        });
        root.addEventListener('mouseover', event => {
            const el = event.target.closest?.('[data-aiw="sug"]');
            const why = this.elements.sugs.querySelector('.aiw-why');
            if (el && why) why.textContent = el.dataset.why || '';
        });
        root.addEventListener('change', event => {
            const el = event.target.closest?.('[data-aiw-field]');
            if (!el) return;
            const p = this.proposals.get(el.dataset.p);
            if (!p || p.state !== 'pending') return;
            if (el.dataset.aiwField === 'addfecha') {
                if (!/^\d{4}-\d{2}-\d{2}$/.test(el.value)) return;
                p.data.picked = [...new Set([...p.data.picked, el.value])].sort();
            } else {
                const map = { hora_inicio: 'start', hora_fin: 'end', desde: 'from', hasta: 'to', aula: 'classroom' };
                p.data[map[el.dataset.aiwField]] = el.value;
            }
            this.recalc(p);
        });
        this.elements.form.addEventListener('submit', event => { event.preventDefault(); this.sendMessage(); });
        this.elements.input.addEventListener('keydown', event => {
            if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); this.sendMessage(); }
        });
        this.elements.panel.addEventListener('keydown', event => { if (event.key === 'Escape') this.collapse(); });
    },

    // ---------- open / collapse ----------
    toggle() { if (this.collapsed) this.open(); else this.collapse(); },
    open() {
        this.collapsed = false; this.unread = false; this.opened = true;
        this.renderAll();
        if (this.available === null) this.loadStatus();
        setTimeout(() => this.elements.input?.focus(), 250);
    },
    collapse() {
        this.collapsed = true;
        this.renderAll();
        document.getElementById('ai-assistant-trigger')?.focus?.();
    },
    startNew() {
        if (this.busy) return;
        if (this.conv.messages.length) this.history.unshift(this.conv);
        this.history = this.history.slice(0, 10);
        this.conv = this.newConversation();
        this.tab = 'chat';
        this.refreshProgram();
        this.renderAll();
        this.elements.input.focus();
    },
    openPast(i) {
        if (this.busy || !this.history[i]) return;
        const past = this.history.splice(i, 1)[0];
        if (this.conv.messages.length) this.history.unshift(this.conv);
        this.conv = past; this.tab = 'chat';
        this.renderAll();
    },

    // ---------- server ----------
    async api(path, options = {}) {
        const response = await fetch(path, { credentials: 'same-origin', cache: 'no-store', ...options,
            headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken(), ...(options.body ? { 'Content-Type': 'application/json' } : {}), ...(options.headers || {}) } });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw Object.assign(new Error(data.error || 'No se pudo completar la operación.'), { status: response.status, data });
        return data;
    },
    async loadStatus() {
        try {
            const data = await this.api('/api/asistente');
            this.available = !!data.available;
            this.suggestions = data.suggestions || null;
        } catch {
            this.available = false;
        }
        this.renderAll();
    },
    async hideSuggestion(key) {
        try { const data = await this.api('/api/asistente/sugerencias/' + encodeURIComponent(key) + '/ocultar', { method: 'POST' }); this.suggestions = data.suggestions; }
        catch (error) { Utils.showToast(error.message, 'error'); }
        this.renderSuggestions();
    },
    async forget() {
        try { const data = await this.api('/api/asistente/sugerencias', { method: 'DELETE' }); this.suggestions = data.suggestions; Utils.showToast('He olvidado lo aprendido. Vuelvo a las sugerencias generales.', 'success'); }
        catch (error) { Utils.showToast(error.message, 'error'); }
        this.renderSuggestions();
    },

    // ---------- suggestions ----------
    suggestionItems() {
        if (this.available === false) return this.LOCAL_LINKS.map(([route, name]) => ({ key: '', label: 'Ir a ' + name, route, learned: false, reason: 'Modo local: solo navegación' }));
        // A learned «Ir a…» only if this role may open that section (as the assistant's own buttons).
        return (this.suggestions?.items || []).filter(s => !s.route || typeof RolePermissions === 'undefined' || RolePermissions.canView(s.route));
    },
    useSuggestion(i) {
        const item = this.suggestionItems()[i];
        if (!item || this.busy) return;
        if (item.route && typeof RolePermissions !== 'undefined' && !RolePermissions.canView(item.route)) return;
        if (item.route) {
            if (item.learned && item.key) this.api('/api/asistente/sugerencias/' + encodeURIComponent(item.key) + '/uso', { method: 'POST' }).catch(() => {});
            this.go(item.route);
        } else if (item.fill) {
            this.elements.input.value = item.fill;
            this.elements.input.focus();
        } else if (item.prompt) {
            this.sendMessage(item.prompt);
        }
    },
    go(route) {
        if (typeof route !== 'string' || !/^[a-z][a-z0-9-]*$/.test(route) || typeof App === 'undefined') return;
        App.navigate(route);
        this.renderContext();
    },

    // ---------- conversation ----------
    pageLabel() {
        // Last breadcrumb («Académico / Programación» → «Programación»), else the menu entry's own label.
        const clean = t => (t || '').replace(/\s+/g, ' ').trim();
        const crumbs = [...document.querySelectorAll('#breadcrumbs .breadcrumb-item')].map(n => clean(n.textContent)).filter(Boolean);
        if (crumbs.length) return crumbs[crumbs.length - 1].slice(0, 80);
        const view = typeof App !== 'undefined' ? App.currentView : '';
        if (!/^[a-z0-9-]+$/.test(view || '')) return '';
        const item = document.querySelector(`.nav-item[data-view="${view}"] .nav-text, .flyout-item[data-view="${view}"]`);
        const label = clean(item?.textContent).replace(/\s+\d+$/, '');
        return label.length <= 40 ? label : '';
    },
    // What is on screen, so «esta formación» or «este mes» mean something. Read from the page, never invented.
    screenContext() {
        const clean = t => String(t ?? '').replace(/[\x00-\x1f\x7f]/g, ' ').replace(/\s+/g, ' ').trim();
        const cut = (t, n) => clean(t).slice(0, n);
        const trail = [...document.querySelectorAll('#breadcrumbs .breadcrumb-item')].map(n => cut(n.textContent, 80)).filter(t => /[\p{L}\p{N}]/u.test(t)).slice(-6);
        const title = cut(document.querySelector('#content-area h1, .view-container h1')?.textContent, 120);
        const path = window.location.pathname.replace(/[^A-Za-z0-9/_.%-]/g, '').slice(0, 160);
        const screen = { path: path.startsWith('/') ? path : '/', trail, title };
        if (typeof App !== 'undefined' && App.currentView === 'calendario-formacion' && typeof ProgramView !== 'undefined' && ProgramView.currentFormation) {
            screen.formation = cut(ProgramView.currentFormation.name, 120);
            const m = ProgramView.currentMonth;
            if (m instanceof Date && !isNaN(m)) screen.month = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`;
            const subject = ProgramView.selectedSubjectId && DataManager.getSubject?.(ProgramView.selectedSubjectId);
            if (subject) screen.subject = cut(subject.name, 120);
        }
        return screen;
    },
    payload() {
        // Text only (no tool data), user first; trimmed to what the server accepts.
        const turns = this.conv.messages.filter(m => !m.error && m.text.trim()).map(m => ({ role: m.role, text: m.text.slice(0, 4000) }));
        let list = turns.slice(-20), total = list.reduce((n, m) => n + m.text.length, 0);
        while (list.length > 1 && (total > 24000 || list[0].role !== 'user')) { total -= list[0].text.length; list = list.slice(1); }
        return { messages: list, page: this.pageLabel(), screen: this.screenContext() };
    },
    async sendMessage(preset) {
        const text = (preset ?? this.elements.input.value).trim();
        if (!text || this.busy) return;
        this.elements.input.value = '';
        this.conv.messages.push({ role: 'user', text, steps: [], nav: [] });
        const bot = { role: 'assistant', text: '', steps: [], nav: [], pending: true };
        this.conv.messages.push(bot);
        this.busy = true;
        this.renderAll();
        if (this.available === null) await this.loadStatus();
        try {
            if (this.available === false) this.answerLocally(bot, text);
            else await this.stream(bot);
        } catch (error) {
            bot.error = error.message || 'No se pudo responder.';
            if (error.status === 503 || error.data?.local) { this.available = false; this.answerLocally(bot, text); }
        } finally {
            bot.pending = false;
            this.busy = false;
            if (this.collapsed) this.unread = true;
            this.renderAll();
        }
    },
    async stream(bot) {
        const response = await fetch('/api/asistente/mensajes', { method: 'POST', credentials: 'same-origin', cache: 'no-store',
            headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() }, body: JSON.stringify(this.payload()) });
        if (!response.ok || !response.body || !(response.headers.get('Content-Type') || '').includes('text/event-stream')) {
            const data = await response.json().catch(() => ({}));
            throw Object.assign(new Error(data.error || 'El asistente no está disponible ahora.'), { status: response.status, data });
        }
        const reader = response.body.getReader(), decoder = new TextDecoder();
        let buffer = '', frame = 0;
        const paint = () => { if (!frame) frame = requestAnimationFrame(() => { frame = 0; this.renderList(); }); };
        for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let cut;
            while ((cut = buffer.indexOf('\n\n')) !== -1) {
                const chunk = buffer.slice(0, cut); buffer = buffer.slice(cut + 2);
                const event = /^event: (.+)$/m.exec(chunk)?.[1], raw = /^data: (.*)$/m.exec(chunk)?.[1];
                let data = {};
                try { data = raw ? JSON.parse(raw) : {}; } catch { continue; }
                if (event === 'text') bot.text += typeof data.t === 'string' ? data.t : '';
                else if (event === 'step' && typeof data.label === 'string') bot.steps.push(data.label);
                else if (event === 'nav' && typeof data.route === 'string') bot.nav.push({ route: data.route, name: String(data.name || data.route) });
                else if (event === 'proposal' && data && data.formation && data.subject) {
                    const p = { id: 'p' + (this.proposals.size + 1) + '-' + Date.now(), data, state: 'pending', clashMode: 'saltar', open: false, savedIds: [], createdSubjects: [], error: '',
                        common: new Set((data.commonCandidates || []).filter(c => c.preselected).map(c => c.formationId)) };
                    this.proposals.set(p.id, p);
                    (bot.proposals = bot.proposals || []).push(p);
                    if (bot.proposals.length === 1) this.openInProgram(p); else this.refreshProgram();
                }
                else if (event === 'done' && data.suggestions) this.suggestions = data.suggestions;
                else if (event === 'error') bot.error = String(data.message || 'El asistente no está disponible ahora.');
                paint();
            }
        }
    },
    answerLocally(bot, text) {
        const local = this.processLocalIntent(text.toLowerCase());
        bot.text = local || 'Ahora no tengo conexión con la IA, así que **no puedo responder preguntas**. Sí puedo llevarte a una sección: elige una abajo o escribe «ir a» y el nombre.';
    },

    // Modo local: navigation by keywords (also the fallback when the AI is unavailable).
    processLocalIntent(input) {
        if (input.includes('ir a') || input.includes('ver') || input.includes('mostrar') || input.includes('llevame') || input.includes('llévame')) {
            const targets = [[['profesor', 'docente'], 'profesores', 'Profesores'], [['programacion', 'programación', 'horario'], 'calendario-formacion', 'Programación lectiva'],
                [['calendario'], 'calendario', 'Calendario general'], [['ajustes', 'configuracion', 'configuración'], 'settings', 'Ajustes'], [['formacion', 'formación', 'curso'], 'formaciones', 'Formaciones'],
                [['distribucion', 'distribución', 'corto'], 'distribucion', 'Distribución de cortos'], [['nomina', 'nómina'], 'nominas', 'Nóminas']];
            for (const [words, route, name] of targets) {
                if (words.some(w => input.includes(w))) {
                    if (typeof App !== 'undefined') App.navigate(route);
                    return `Te llevo a **${name}**.`;
                }
            }
        }
        if (input.includes('generar datos')) return 'La generación de datos de prueba se ha retirado de la aplicación.';
        return null;
    },

    // ---------- programar clases: propuesta, vista previa, guardar y deshacer ----------
    // The server builds the proposal (/api/asistente/propuesta); saving uses the same path as the
    // Programación screen (DataManager → /api/programming/classes) and only after the user confirms.
    proposalParams(p) {
        const d = p.data, base = { formationId: d.formation.id, subjectId: d.subject.id, hora_inicio: d.start, hora_fin: d.end, aula: d.classroom || '' };
        return d.mode === 'fechas' ? { ...base, fechas: d.picked } : { ...base, dias: d.days, desde: d.from, hasta: d.to };
    },
    pendingProposals() { return (this.conv?.messages || []).flatMap(m => m.proposals || []).filter(p => p.state === 'pending'); },
    takenDates(p) { return p.data.dates.filter(d => d.status === 'ok' || (d.status === 'classroom' && p.clashMode === 'sinaula')); },
    recalc(p) {
        // Each edit gets a number; an answer for an older edit, or one that arrives after saving,
        // is dropped so it can never overwrite a newer edit or a saved proposal.
        clearTimeout(p.timer);
        const seq = p.seq = (p.seq || 0) + 1;
        p.timer = setTimeout(async () => {
            if (p.state !== 'pending' || seq !== p.seq) return;
            try {
                const fresh = (await this.api('/api/asistente/propuesta', { method: 'POST', body: JSON.stringify(this.proposalParams(p)) })).proposal;
                if (p.state !== 'pending' || seq !== p.seq) return;
                p.data = fresh; p.error = '';
                const ids = new Set((p.data.commonCandidates || []).map(c => c.formationId));
                p.common = new Set([...p.common].filter(id => ids.has(id)));
            }
            catch (error) { if (seq === p.seq) p.error = error.message; }
            this.renderList(); this.refreshProgram();
        }, 350);
    },
    async proposalAction(act, el) {
        const p = this.proposals.get(el.dataset.p);
        if (!p) return;
        if (act === 'p-day' && p.state === 'pending') {
            const d = Number(el.dataset.d), days = new Set(p.data.days);
            days.has(d) ? days.delete(d) : days.add(d);
            if (!days.size) return;
            p.data.days = [...days].sort(); this.recalc(p);
        } else if (act === 'p-rmdate' && p.state === 'pending') {
            if (p.data.picked.length <= 1) return;
            p.data.picked = p.data.picked.filter(x => x !== el.dataset.date); this.recalc(p);
        } else if (act === 'p-common' && p.state === 'pending') {
            const f = el.dataset.f;
            p.common.has(f) ? p.common.delete(f) : p.common.add(f);
            this.refreshProgram();
        } else if (act === 'p-clash' && p.state === 'pending') { p.clashMode = el.dataset.mode === 'sinaula' ? 'sinaula' : 'saltar'; this.refreshProgram(); }
        else if (act === 'p-dates') p.open = !p.open;
        else if (act === 'p-goto') this.openInProgram(p);
        else if (act === 'p-discard' && p.state === 'pending') { p.state = 'discarded'; this.refreshProgram(); }
        else if (act === 'p-confirm') await this.saveProposal(p);
        else if (act === 'p-undo') await this.undoProposal(p);
        this.renderList();
    },
    // Waits for the change queue and tells whether the server copy has (or no longer has) the ids.
    async synced(key, ids, present) {
        await FileStorage._changeQueue;
        const copy = FileStorage._serverCopy?.[key];
        return !!copy && ids.every(id => copy.has(String(id)) === present);
    },
    // Takes items back out of the local store (no server change: they were never confirmed there).
    dropLocal(key, ids) {
        const gone = new Set(ids);
        DataManager.saveAll(key, DataManager.getAll(key).filter(x => !gone.has(x.id)));
    },
    async saveProposal(p) {
        if (p.state !== 'pending') return;
        clearTimeout(p.timer); p.seq = (p.seq || 0) + 1;
        p.state = 'saving'; p.error = ''; this.renderList();
        let created = [], items = [];
        try {
            // Fresh check right before saving: someone may have used the room or the professor meanwhile.
            p.data = (await this.api('/api/asistente/propuesta', { method: 'POST', body: JSON.stringify(this.proposalParams(p)) })).proposal;
            const take = this.takenDates(p), d = p.data;
            if (!take.length) throw new Error('No queda ninguna fecha libre que programar.');
            const now = new Date().toISOString();
            const item = (x, subjectId, formationId, professorId) => ({ id: Utils.generateUUID(), subjectId, formationId, professorId: professorId || null,
                dayOfWeek: x.dow, startTime: d.start, endTime: d.end, classroom: x.status === 'classroom' ? '' : (d.classroom || ''), startDate: x.date, endDate: x.date, color: null, createdAt: now });
            // Common subject: the other formations chosen in the card. Missing subjects are created
            // as in Programación (same name, professor, colour, hours and timetable).
            const targets = (d.commonCandidates || []).filter(c => p.common.has(c.formationId)).map(t => ({ ...t }));
            const source = DataManager.getSubject?.(d.subject.id) || {};
            for (const t of targets.filter(t => !t.subjectId)) {
                const subject = DataManager.createSubject({ name: d.subject.name, formationId: t.formationId, professorId: d.subject.professorId, color: d.subject.color || source.color,
                    description: source.description, defaultStartTime: source.defaultStartTime, defaultEndTime: source.defaultEndTime, type: source.type || 'COMÚN',
                    totalHours: d.subject.totalHours || 0, studyBlock: source.studyBlock || 'OTROS' });
                t.subjectId = subject.id; created.push(subject.id);
            }
            if (created.length && !await this.synced('subjects', created, true)) throw new Error('No se pudo crear la asignatura en las otras formaciones. No se ha programado nada.');
            items = take.map(x => item(x, d.subject.id, d.formation.id, d.subject.professorId));
            for (const t of targets) for (const x of take) if (!t.skip.includes(x.date)) items.push(item(x, t.subjectId, t.formationId, d.subject.professorId));
            DataManager.saveAll(DataManager.KEYS.CLASSES, DataManager.getAll(DataManager.KEYS.CLASSES).concat(items));
            if (!await this.synced('classes', items.map(it => it.id), true)) throw new Error('No se pudo guardar la programación. No se ha aplicado ningún cambio.');
            p.createdSubjects = created; p.formations = 1 + targets.length;
            p.savedIds = items.map(it => it.id); p.saved = items.length; p.state = 'saved';
        } catch (error) {
            // Leave nothing half done: rejected classes leave the calendar, subjects created for them are removed.
            if (items.length) this.dropLocal(DataManager.KEYS.CLASSES, items.map(it => it.id));
            let message = error.message || 'No se pudo guardar.';
            if (created.length) {
                this.dropLocal(DataManager.KEYS.SUBJECTS, created);
                if (!await this.synced('subjects', created, false)) message += ' Además, no se pudo quitar la asignatura creada en las otras formaciones: revísalo en Plan de estudios.';
            }
            p.state = 'pending'; p.error = message;
        }
        this.refreshProgram();
    },
    async undoProposal(p) {
        if (p.state !== 'saved' || !p.savedIds.length) return;
        p.state = 'saving'; this.renderList();
        DataManager.saveAll(DataManager.KEYS.CLASSES, DataManager.getAll(DataManager.KEYS.CLASSES).filter(c => !p.savedIds.includes(c.id)));
        const classesGone = await this.synced('classes', p.savedIds, false);
        let subjectsGone = true;
        if (classesGone && p.createdSubjects.length) {
            this.dropLocal(DataManager.KEYS.SUBJECTS, p.createdSubjects);
            subjectsGone = await this.synced('subjects', p.createdSubjects, false);
        }
        if (!classesGone || !subjectsGone) {
            p.state = 'saved';
            p.error = classesGone ? 'Las clases se quitaron, pero no la asignatura creada en las otras formaciones: revísalo en Plan de estudios.' : 'No se pudo deshacer. Revisa la programación y vuelve a intentarlo.';
            this.refreshProgram(); return;
        }
        p.savedIds = []; p.createdSubjects = []; p.state = 'pending'; p.error = '';
        this.recalc(p);
        this.refreshProgram();
    },
    refreshProgram() {
        if (typeof App !== 'undefined' && App.currentView === 'calendario-formacion' && typeof ProgramView !== 'undefined' && document.getElementById('calendar-grid')) ProgramView.renderCalendar();
    },
    async openInProgram(p) {
        if (typeof App === 'undefined' || typeof ProgramView === 'undefined') return;
        if (App.currentView !== 'calendario-formacion') App.navigate('calendario-formacion');
        for (let t = 0; t < 50 && !(document.getElementById('calendar-grid') && ProgramView.currentFormation); t++) await new Promise(r => setTimeout(r, 100));
        if (!document.getElementById('calendar-grid')) return;
        if (String(ProgramView.currentFormation?.id) !== String(p.data.formation.id) && DataManager.getFormation?.(p.data.formation.id)) ProgramView.switchFormation(p.data.formation.id);
        const first = p.data.dates[0]?.date;
        if (first) { const [y, m] = first.split('-').map(Number); ProgramView.currentMonth = new Date(y, m - 1, 1); }
        ProgramView.renderCalendar();
        this.renderContext();
    },
    // Called by ProgramView.renderCalendar(): marks the proposal on the month and in «Asignaturas del mes».
    decorateProgram(view) {
        const grid = document.getElementById('calendar-grid');
        if (!grid || !view?.currentFormation) return;
        const here = String(view.currentFormation.id);
        const mine = this.pendingProposals().filter(p => String(p.data.formation.id) === here || p.common.has(here));
        const y = view.currentMonth.getFullYear(), m = view.currentMonth.getMonth();
        const list = document.getElementById('subjects-list');
        for (const p of mine) {
            let added = 0;
            // On another formation of a common subject, the days it already has are not added there.
            const target = String(p.data.formation.id) === here ? null : (p.data.commonCandidates || []).find(c => String(c.formationId) === here);
            for (const d of p.data.dates) {
                const [Y, M, D] = d.date.split('-').map(Number);
                if (Y !== y || M - 1 !== m) continue;
                const cell = grid.querySelector(`.calendar-day[data-day="${D}"][data-month="${m}"][data-year="${y}"]`);
                if (!cell) continue;
                const already = !!target && target.skip.includes(d.date);
                const add = !already && (d.status === 'ok' || (d.status === 'classroom' && p.clashMode === 'sinaula'));
                cell.classList.add(add ? (d.status === 'classroom' ? 'aiw-prev-clash' : 'aiw-prev') : 'aiw-prev-skip');
                cell.title = add ? `Propuesta (sin guardar): ${p.data.subject.name} ${p.data.start}–${p.data.end}${d.status === 'classroom' ? ' · sin aula' : ''}` : (already ? 'Ya tiene esta clase ese día' : d.detail);
                if (add) {
                    added++;
                    if (!cell.querySelector('.aiw-prev-plus')) {
                        const plus = document.createElement('span');
                        plus.className = 'aiw-prev-plus'; plus.textContent = '+'; plus.setAttribute('aria-hidden', 'true');
                        cell.appendChild(plus);
                    }
                }
            }
            if (!list) continue;
            const row = document.createElement('div');
            row.className = 'aiw-prev-row';
            const sw = document.createElement('span'); sw.className = 'aiw-prev-sw'; sw.style.background = /^#[0-9a-fA-F]{3,8}$/.test(p.data.subject.color || '') ? p.data.subject.color : '#2DD4BF';
            const body = document.createElement('div');
            const name = document.createElement('b'); name.textContent = p.data.subject.name;
            const tag = document.createElement('span'); tag.className = 'aiw-prev-tag'; tag.textContent = `Propuesta · ${added} ${added === 1 ? 'clase' : 'clases'} este mes`;
            const prof = document.createElement('div'); prof.className = 'aiw-prev-prof'; prof.textContent = p.data.subject.professorName || 'Sin profesor';
            body.append(name, tag, prof);
            const room = document.createElement('span'); room.className = 'aiw-prev-room'; room.textContent = p.data.classroom || 'Sin aula';
            row.append(sw, body, room);
            list.prepend(row);
        }
    },
    renderProposal(p) {
        const i = p.id;
        const d = p.data, s = d.subject, esc = v => Utils.sanitizeHTML(v ?? ''), edit = p.state === 'pending';
        const by = st => d.dates.filter(x => x.status === st);
        const hol = by('holiday'), prof = by('professor'), dup = by('duplicate'), room = by('classroom');
        const adds = this.takenDates(p).length, hours = Math.round(adds * d.hoursPerClass * 10) / 10;
        const fmt = iso => { const [y, m, dd] = iso.split('-').map(Number); return `${['', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom'][((new Date(Date.UTC(y, m - 1, dd)).getUTCDay() + 6) % 7) + 1]} ${dd}/${m}`; };
        const list = arr => arr.slice(0, 6).map(x => fmt(x.date)).join(', ') + (arr.length > 6 ? ` y ${arr.length - 6} más` : '');
        const pct = s.totalHours ? Math.min(100, (s.scheduledHours + (p.state === 'saved' ? 0 : hours)) / s.totalHours * 100) : 0;
        const day = (n, l) => `<button type="button" class="aiw-day${d.days.includes(n) ? ' aiw-day-on' : ''}" data-aiw="p-day" data-p="${i}" data-d="${n}" aria-pressed="${d.days.includes(n)}" ${edit ? '' : 'disabled'}>${l}</button>`;
        const field = (name, type, value, label, w) => `<input class="aiw-field" type="${type}" data-aiw-field="${name}" data-p="${i}" value="${esc(value)}" aria-label="${label}" style="width:${w}px" ${edit ? '' : 'disabled'}>`;
        const notes = [
            hol.length ? `<div class="aiw-pnote">${hol.length === 1 ? 'Festivo' : hol.length + ' festivos'} (${list(hol)}): no se programa${hol.length === 1 ? '' : 'n'}.</div>` : '',
            dup.length ? `<div class="aiw-pnote">Ya programada ${list(dup)}: se salta${dup.length === 1 ? '' : 'n'}.</div>` : '',
            prof.length ? `<div class="aiw-pnote aiw-pnote-red"><b>Profesor ocupado</b> ${list(prof)}: no se programa${prof.length === 1 ? '' : 'n'}.<br>${esc(prof[0].detail)}</div>` : '',
            room.length ? `<div class="aiw-pnote aiw-pnote-red"><b>${esc(d.classroom)} ocupada</b> ${list(room)}. ${esc(room[0].detail)}
                <div class="aiw-pbtns"><button type="button" class="aiw-pbtn${p.clashMode === 'saltar' ? ' aiw-pbtn-on' : ''}" data-aiw="p-clash" data-mode="saltar" data-p="${i}" ${edit ? '' : 'disabled'}>Saltar esos días</button><button type="button" class="aiw-pbtn${p.clashMode === 'sinaula' ? ' aiw-pbtn-on' : ''}" data-aiw="p-clash" data-mode="sinaula" data-p="${i}" ${edit ? '' : 'disabled'}>Programarlos sin aula</button></div></div>` : '',
            (d.commonCandidates || []).length ? `<div class="aiw-pnote aiw-pcommon"><b>Asignatura común.</b> ¿La añado también en las formaciones del mismo curso?
                <div class="aiw-pchecks">${d.commonCandidates.map(c => `<label class="aiw-pcheck"><input type="checkbox" data-aiw="p-common" data-p="${i}" data-f="${esc(c.formationId)}" ${p.common.has(c.formationId) ? 'checked' : ''} ${edit ? '' : 'disabled'}><span>${esc(c.name)}${c.subjectId ? '' : ' <em>(se crea la asignatura)</em>'}${c.skip.length ? ` <em>(ya la tiene ${c.skip.length} ${c.skip.length === 1 ? 'día' : 'días'})</em>` : ''}</span></label>`).join('')}</div></div>`
                : (s.common ? `<div class="aiw-pnote">Asignatura común, pero no hay otras formaciones de ese curso.</div>` : '')
        ].join('');
        const dates = p.open ? `<ul class="aiw-pdates">${d.dates.map(x => `<li class="aiw-pdate-${x.status}"><span>${fmt(x.date)}</span><span>${x.status === 'ok' ? '✓' : esc(x.detail)}</span></li>`).join('')}</ul>` : '';
        const actions = p.state === 'saved'
            ? `<div class="aiw-psaved"><b>✓ ${p.saved} ${p.saved === 1 ? 'clase programada' : 'clases programadas'} en ${p.formations > 1 ? p.formations + ' formaciones' : esc(d.formation.name)}</b><button type="button" class="aiw-btn" data-aiw="p-undo" data-p="${i}">Deshacer</button><button type="button" class="aiw-link" data-aiw="p-goto" data-p="${i}">Ver →</button></div>`
            : p.state === 'discarded' ? '<p class="aiw-hint aiw-pdone">Descartada. No se ha guardado nada.</p>'
            : `<div class="aiw-pactions"><button type="button" class="aiw-send aiw-pconfirm" data-aiw="p-confirm" data-p="${i}" ${p.state === 'saving' || !adds ? 'disabled' : ''}>${p.state === 'saving' ? 'Guardando…' : `Programar ${adds} ${adds === 1 ? 'clase' : 'clases'}${p.common.size ? ` en ${p.common.size + 1} formaciones` : ''}`}</button><button type="button" class="aiw-btn aiw-pdiscard" data-aiw="p-discard" data-p="${i}" ${p.state === 'saving' ? 'disabled' : ''}>Descartar</button></div>`;
        return `<div class="aiw-prop">
            <div class="aiw-prop-head"><span class="aiw-label aiw-amber">${p.state === 'saved' ? 'Guardada' : 'Propuesta · sin guardar'}</span><button type="button" class="aiw-link" data-aiw="p-goto" data-p="${i}">← Ver en Programación</button></div>
            <div class="aiw-pgrid">
                <span>Formación</span><b>${esc(d.formation.name)}</b>
                <span>Asignatura</span><span><b>${esc(s.name)}</b> · ${esc(s.professorName || 'sin profesor')}</span>
                ${d.mode === 'fechas'
                    ? `<span>Fechas</span><div class="aiw-chips-dates">${d.picked.map(x => `<span class="aiw-dchip">${fmt(x)}${edit && d.picked.length > 1 ? `<button type="button" data-aiw="p-rmdate" data-p="${i}" data-date="${esc(x)}" aria-label="Quitar ${fmt(x)}">×</button>` : ''}</span>`).join('')}${edit ? `<input class="aiw-field" type="date" data-aiw-field="addfecha" data-p="${i}" aria-label="Añadir una fecha" title="Añadir una fecha" style="width:132px">` : ''}</div>`
                    : `<span>Días</span><div class="aiw-days">${day(1, 'L')}${day(2, 'M')}${day(3, 'X')}${day(4, 'J')}${day(5, 'V')}${day(6, 'S')}${day(7, 'D')}</div>
                <span>Fechas</span><div class="aiw-frow">${field('desde', 'date', d.from, 'Desde', 132)}<span>a</span>${field('hasta', 'date', d.to, 'Hasta', 132)}</div>`}
                <span>Horario</span><div class="aiw-frow">${field('hora_inicio', 'time', d.start, 'Hora de inicio', 96)}<span>a</span>${field('hora_fin', 'time', d.end, 'Hora de fin', 96)}${d.timeFrom && d.timeFrom !== 'indicado' ? `<span class="aiw-hint">habitual de la ${esc(d.timeFrom)}</span>` : ''}</div>
                <span>Aula</span><div>${field('aula', 'text', d.classroom, 'Aula', 180)}</div>
            </div>
            <div class="aiw-phours"><div><b>${adds} ${adds === 1 ? 'clase' : 'clases'} · ${hours} h</b><span>${s.totalHours ? `previstas ${s.totalHours} h · ya programadas ${s.scheduledHours} h` : `ya programadas ${s.scheduledHours} h`}</span></div>${s.totalHours ? `<div class="aiw-pbar"><span style="width:${pct.toFixed(1)}%"></span></div>` : ''}</div>
            ${notes}
            <button type="button" class="aiw-link aiw-pdates-btn" data-aiw="p-dates" data-p="${i}" aria-expanded="${p.open ? 'true' : 'false'}">${p.open ? 'Ocultar las fechas ▴' : 'Ver las fechas una a una ▾'}</button>${dates}
            ${p.error ? `<div class="aiw-error">${esc(p.error)}</div>` : ''}
            ${actions}
        </div>`;
    },

    // ---------- rendering ----------
    // Untrusted text (user and model) is escaped first; only bold, line breaks, lists and simple tables are added.
    formatText(text) {
        const inline = s => s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
        const lines = Utils.sanitizeHTML(String(text ?? '')).split('\n');
        const out = [];
        let para = [], list = null, table = null;
        const flush = () => {
            if (para.length) { out.push('<p>' + para.map(inline).join('<br>') + '</p>'); para = []; }
            if (list) { out.push(`<${list.tag}>` + list.items.map(i => '<li>' + inline(i) + '</li>').join('') + `</${list.tag}>`); list = null; }
            if (table) {
                const [head, ...body] = table;
                out.push('<div class="aiw-table-wrap"><table class="aiw-table"><thead><tr>' + head.map(c => '<th>' + inline(c) + '</th>').join('') + '</tr></thead><tbody>'
                    + body.map(r => '<tr>' + r.map(c => '<td>' + inline(c) + '</td>').join('') + '</tr>').join('') + '</tbody></table></div>');
                table = null;
            }
        };
        for (const line of lines) {
            const t = line.trim();
            if (/^\|.*\|$/.test(t)) {
                if (list || para.length) flush();
                const cells = t.slice(1, -1).split('|').map(c => c.trim());
                if (cells.every(c => /^:?-{2,}:?$/.test(c))) continue;
                (table = table || []).push(cells);
                continue;
            }
            if (table) flush();
            const bullet = /^[-*•]\s+(.*)$/.exec(t), number = /^\d+[.)]\s+(.*)$/.exec(t);
            if (bullet || number) {
                const tag = bullet ? 'ul' : 'ol';
                if (para.length || (list && list.tag !== tag)) flush();
                (list = list || { tag, items: [] }).items.push((bullet || number)[1]);
                continue;
            }
            if (!t) { flush(); continue; }
            if (list) flush();
            para.push(/^#{1,4}\s+/.test(t) ? '**' + t.replace(/^#{1,4}\s+/, '') + '**' : t);
        }
        flush();
        return out.join('');
    },

    renderAll() {
        const el = this.elements;
        if (!el.panel) return;
        el.panel.classList.toggle('aiw-collapsed', this.collapsed);
        el.panel.setAttribute('aria-hidden', this.collapsed ? 'true' : 'false');
        el.panel.inert = this.collapsed;
        const local = this.available === false;
        el.badge.textContent = this.available === null ? '…' : (local ? 'Modo local' : 'Con IA');
        el.badge.classList.toggle('aiw-badge-local', local);
        const chat = this.tab === 'chat';
        el.chat.hidden = !chat; el.hist.hidden = chat;
        el.tabChat.setAttribute('aria-selected', String(chat)); el.tabHist.setAttribute('aria-selected', String(!chat));
        el.tabChat.classList.toggle('aiw-tab-on', chat); el.tabHist.classList.toggle('aiw-tab-on', !chat);
        el.send.disabled = this.busy;
        el.input.placeholder = local ? 'Escribe «ir a» y una sección' : 'Pregunta sobre esta página o cualquier otra cosa de SICAF';
        this.renderContext(); this.renderList(); this.renderSuggestions(); this.renderHistory(); this.renderEdge();
    },
    renderContext() {
        const page = this.pageLabel();
        SafeHTML.set(this.elements.context, page
            ? `${this.icon('pin')}<span>Estás en <b>${Utils.sanitizeHTML(page)}</b>. Pregunta sobre lo que ves.</span>`
            : `${this.icon('pin')}<span>Pregunta sobre clases, profesores o Distribución.</span>`);
    },
    renderList() {
        const msgs = this.conv.messages;
        if (!msgs.length) {
            const name = Utils.sanitizeHTML((AuthManager.getCurrentUser?.()?.name || '').split(' ')[0]);
            SafeHTML.set(this.elements.list, `<div class="aiw-hello"><b>Hola${name ? ', ' + name : ''}.</b> ${this.available === false
                ? 'Ahora estoy en <b>Modo local</b>: puedo llevarte a una sección, pero no responder preguntas.'
                : 'Pregúntame por clases, horarios, profesores o el estado de Distribución. Solo consulto: no cambio nada.'}</div>`);
            return;
        }
        SafeHTML.set(this.elements.list, msgs.map((m, i) => m.role === 'user'
            ? `<div class="aiw-user">${Utils.sanitizeHTML(m.text)}</div>`
            : this.renderBot(m, i)).join(''));
        this.elements.list.scrollTop = this.elements.list.scrollHeight;
    },
    renderBot(m, i) {
        const thinking = m.pending && !m.text;
        const steps = m.steps.length ? (thinking
            ? `<div class="aiw-steps aiw-steps-live"><span class="aiw-dot"></span><span>${Utils.sanitizeHTML(m.steps[m.steps.length - 1])}…</span></div>`
            : `<button type="button" class="aiw-steps" data-aiw="steps" data-i="${i}" aria-expanded="${m.open ? 'true' : 'false'}"><span class="aiw-ok">✓</span><span>${m.steps.length} ${m.steps.length === 1 ? 'consulta' : 'consultas'}</span><span class="aiw-grow"></span><span>${m.open ? '▴' : '▾'}</span></button>`
                + (m.open ? `<ol class="aiw-step-list">${m.steps.map(s => `<li>${Utils.sanitizeHTML(s)}</li>`).join('')}</ol>` : ''))
            : (thinking ? '<div class="aiw-steps aiw-steps-live"><span class="aiw-dot"></span><span>Pensando…</span></div>' : '');
        // Only sections this role may open (the menu matrix); the route guard checks again.
        const allowed = n => typeof RolePermissions === 'undefined' || RolePermissions.canView(n.route);
        const nav = m.nav.filter(allowed).map(n => `<button type="button" class="aiw-nav" data-aiw="nav" data-route="${Utils.sanitizeHTML(n.route)}"><span>${Utils.sanitizeHTML(n.name)}</span><span class="aiw-nav-go">Ir →</span></button>`).join('');
        const error = m.error ? `<div class="aiw-error">${Utils.sanitizeHTML(m.error)}</div>` : '';
        const card = (m.proposals || []).map(p => this.renderProposal(p)).join('');
        return `<article class="aiw-bot">${steps}${m.text ? `<div class="aiw-text">${this.formatText(m.text)}</div>` : ''}${card}${nav ? `<div class="aiw-navs">${nav}</div>` : ''}${error}</article>`;
    },
    renderSuggestions() {
        const items = this.suggestionItems(), learned = this.available !== false && !!this.suggestions?.learned;
        // Only before the first question: once the conversation starts, the chat gets the room («＋» brings them back).
        this.elements.sugs.hidden = this.conv.messages.length > 0;
        if (!items.length || this.conv.messages.length) { SafeHTML.set(this.elements.sugs, ''); return; }
        const title = this.available === false ? 'Ir a' : (learned ? 'Para ti' : 'Para empezar');
        const hint = this.available === false ? '' : (learned ? 'Según lo que sueles preguntar' : 'Iré aprendiendo lo que sueles preguntar');
        SafeHTML.set(this.elements.sugs, `
            <div class="aiw-sugs-head"><span class="aiw-label">${title}</span><span class="aiw-hint">${hint}</span><span class="aiw-grow"></span>
                ${learned ? '<button type="button" class="aiw-link" data-aiw="forget">Olvidar lo aprendido</button>' : ''}</div>
            <div class="aiw-chips">${items.map((s, i) => `<span class="aiw-chip${s.learned ? ' aiw-chip-learned' : ''}">
                <button type="button" class="aiw-chip-main" data-aiw="sug" data-i="${i}" data-why="${Utils.sanitizeHTML(s.reason || '')}" title="${Utils.sanitizeHTML(s.reason || '')}" ${this.busy ? 'disabled' : ''}>${Utils.sanitizeHTML(s.label)}${s.learned && s.count ? `<span class="aiw-count">×${Number(s.count)}</span>` : ''}</button>
                ${s.learned ? `<button type="button" class="aiw-chip-x" data-aiw="hide" data-key="${Utils.sanitizeHTML(s.key)}" aria-label="No sugerir «${Utils.sanitizeHTML(s.label)}»" title="No me la sugieras">✕</button>` : ''}
            </span>`).join('')}</div>
            ${learned ? '<p class="aiw-why"></p>' : ''}`);
    },
    renderHistory() {
        const time = d => new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        SafeHTML.set(this.elements.hist, (this.history.length ? this.history.map((c, i) => {
            const first = c.messages.find(m => m.role === 'user')?.text || 'Conversación';
            return `<button type="button" class="aiw-past" data-aiw="past" data-i="${i}"><b>${Utils.sanitizeHTML(first.slice(0, 70))}</b><span>${time(c.startedAt)} · ${c.messages.filter(m => m.role === 'user').length} preguntas</span></button>`;
        }).join('') : '<p class="aiw-hello">Aún no hay conversaciones anteriores. Al pulsar «＋» la actual se guarda aquí.</p>')
            + '<p class="aiw-hint">Solo se guarda en esta pestaña; se borra al recargar o cerrar sesión.</p>');
    },
    renderEdge() {
        const el = this.elements, show = this.collapsed && this.opened;
        el.edge.hidden = !show;
        el.edgeNote.hidden = !show || (!this.busy && !this.unread);
        SafeHTML.set(el.edgeState, this.busy ? '<span class="aiw-dot" title="Pensando"></span>' : (this.unread ? '<span class="aiw-unread">1</span>' : ''));
        el.edgeNote.textContent = this.busy ? 'Pensando… te aviso al terminar' : (this.unread ? 'Respuesta lista · pulsa para verla' : '');
        const trigger = document.getElementById('ai-assistant-trigger');
        trigger?.classList.toggle('aiw-trigger-open', !this.collapsed);
        trigger?.classList.toggle('aiw-trigger-news', this.collapsed && (this.busy || this.unread));
        trigger?.setAttribute('aria-expanded', String(!this.collapsed));
    }
};

window.AIAssistant = AIAssistant;
